import React from 'react'
import invariant from 'tiny-invariant'
import { SvgRemove } from '@chilipiper/icons/src/design-system'
import { createContextWithHook, typeValidator } from '@chilipiper/utils'
import { Flex, Text } from '../../../core-components'
import { Button, IconButton, OptionButton } from '../../action'
import { CoreModal, CoreModalContent } from '../core-modal/CoreModal'

type Props = Omit<React.ComponentProps<typeof CoreModal>, 'ModalSpecificProvider' | 'size'> & {
  size: 26 | 28 | 31
}

export const { useConfigurationModalContext, ConfigurationModalBaseProvider } =
  createContextWithHook<{
    onClose: () => void
    titleId: string
  }>()('ConfigurationModal')

export const ConfigurationModal = (props: Props) => {
  return <CoreModal {...props} ModalSpecificProvider={ConfigurationModalBaseProvider} />
}

const Header = ({
  breadcrumbs,
  title,
  description,
}: {
  breadcrumbs?: string[]
  description?: string
  title: string
}) => {
  const { titleId, onClose } = useConfigurationModalContext()
  return (
    <Flex px={7} py={6} gap={7} justifyContent='space-between' flexGrow={1}>
      <Flex flexDirection='column' gap={2} alignSelf='center'>
        <Flex id={titleId} gap={2}>
          {breadcrumbs?.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <Text textStyle='heading-m' color='fg/moderate'>
                {breadcrumb}
              </Text>
              <Text textStyle='heading-m' color='fg/moderate'>
                /
              </Text>
            </React.Fragment>
          ))}
          <Text textStyle='heading-m' color='fg/major'>
            {title}
          </Text>
        </Flex>
        {description && (
          <Text textStyle='body-multiline' color='fg/moderate'>
            {description}
          </Text>
        )}
      </Flex>
      <IconButton
        icon={SvgRemove}
        label='Close'
        size={7}
        variant='moderate'
        onClick={onClose}
        hasTooltip={false}
      />
    </Flex>
  )
}

const Footer = ({
  onBack,
  primaryButton,
  secondaryButton,
}: {
  onBack?: () => void
  primaryButton?: React.ReactNode | ((close: () => void) => React.ReactNode)
  secondaryButton?: React.ReactNode | ((close: () => void) => React.ReactNode)
}) => {
  const { onClose } = useConfigurationModalContext()

  const primaryButtonNode =
    primaryButton && typeof primaryButton === 'function' ? primaryButton(onClose) : primaryButton
  const secondaryButtonNode =
    secondaryButton && typeof secondaryButton === 'function'
      ? secondaryButton(onClose)
      : secondaryButton

  invariant(
    !primaryButtonNode ||
      typeValidator(Button)(primaryButtonNode) ||
      typeValidator(OptionButton)(primaryButtonNode),
    'primaryButton must be a Button or OptionButton component'
  )
  invariant(
    !secondaryButtonNode || typeValidator(Button)(secondaryButtonNode),
    'secondaryButton must be a Button component or undefined'
  )
  return (
    <Flex px={7} py={5} justifyContent='space-between' gap={3}>
      <Button variant='minor' onClick={onBack ?? onClose}>
        {onBack ? 'Back' : 'Cancel'}
      </Button>
      <Flex gap={3}>
        {secondaryButtonNode}
        {primaryButtonNode}
      </Flex>
    </Flex>
  )
}

type CoreModalContentProps = React.ComponentProps<typeof CoreModalContent>
const ConfigurationModalContent = (
  props: Omit<CoreModalContentProps, 'children'> & {
    children: NonNullable<CoreModalContentProps['children']>
  }
) => <CoreModalContent {...props} />

ConfigurationModal.Header = Header
ConfigurationModal.Footer = Footer
ConfigurationModal.Content = ConfigurationModalContent
